import cmTimeouts from '../../consts/config/cmTimeouts';
import {setYapDeviceConnected, setYapHolderConnected} from '../../state/ducks/yapEncrypted';
import {makeSelectAssetIdByType} from '../../state/selectors/yapEncrypted';
import {makeSelectIsYapDeviceConnected, makeSelectIsYapHolderConnected} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import converter from '../../utils/converter';
import analyticsService from '../analyticsService';
import backendService from '../app/backendService';
import gamClientService from '../gam/gamClientService';
import log from '../logger/log';
import pairingService from '../pairingService';
import uamClientService from '../uam/uamClientService';
import cmMessageRequestService from './cmMessageRequestService';
import cmMessageResponseService from './cmMessageResponseService';
import cmService from './cmService';

let cmDeviceReconnectTimeout;
let cmHolderReconnectTimeout;
const clearReconnectTimeout = (isHolder) =>
    clearTimeout(isHolder ? cmHolderReconnectTimeout : cmDeviceReconnectTimeout);

const initCmClient = async (isHolder) => {
    if (!isDeviceConnected()) return;

    clearReconnectTimeout(isHolder);

    if (isCmClientConnected(isHolder)) {
        const isYapDeviceConnected = isHolder
            ? makeSelectIsYapHolderConnected()(getState())
            : makeSelectIsYapDeviceConnected()(getState());

        if (!isYapDeviceConnected) {
            cmMessageRequestService.publishInitializationEvent(isHolder);
        }

        return;
    }

    log.info(`Connect to CM is started. isHolder: ${isHolder}`);

    await connectToCmClient(isHolder);
};

const isDeviceConnected = () => {
    if (pairingService.isDeviceConnected()) return true;

    log.info('Connect to CM is ignored. Device is not connected.');
    return false;
};

const connectToCmClient = async (isHolder) => {
    const onError = () => {
        disconnectCmClient(!!isHolder, false);

        const timeout = setTimeout(() => initCmClient(isHolder), cmTimeouts.CM_RECONNECT_TIMEOUT * 1000);

        if (isHolder) {
            cmHolderReconnectTimeout = timeout;
        } else {
            cmDeviceReconnectTimeout = timeout;
        }
    };

    const cmClient = cmService.getCmClient(isHolder)();

    if (cmClient) {
        await cmClient.reconnectClient(async () => {
            //init new connection if reconnect can not be done
            await initNewCmConnection(isHolder, onError);
        });
        return;
    }

    await initNewCmConnection(isHolder, onError);
};

const initNewCmConnection = async (isHolder, onError) => {
    const assetId = makeSelectAssetIdByType(isHolder)(getState());

    if (!assetId) return;

    const isGamBackend = backendService.isGamBackend();
    const type = cmService.getDeviceType();

    analyticsService.pushConnectIotPlatformEvent(type);
    const cmSettings = isGamBackend
        ? await gamClientService.connectGamDevice(assetId)
        : await uamClientService.connectDevice(assetId);

    if (!cmSettings) return;

    if (!isDeviceConnected()) return;

    const cmClient = cmService.getCmClient(isHolder)(assetId, cmSettings);

    if (!cmClient) return;

    const onCloseHandler = () => {
        if (isHolder) {
            dispatch(setYapHolderConnected(false));
        } else {
            dispatch(setYapDeviceConnected(false));
        }
    };

    const onConnectHandler = () => {
        subscribeOnMessage(isHolder);
    };

    cmClient.attachHandlers(onConnectHandler, onCloseHandler, onError);
    cmClient.connect();
};

const disconnectCmClient = (isHolder, isClearReconnectData = true) => {
    let cmClient;

    const disconnectClient = (isHolder) => {
        cmClient = cmService.getCmClient(isHolder)();
        clearReconnectTimeout(isHolder);
        if (cmClient) {
            if (isClearReconnectData) {
                cmMessageRequestService.publishDisconnectEvent(isHolder);
                cmClient.disconnectAndClear();
            } else {
                cmClient.disconnect();
            }
        }
    };

    if (isHolder === undefined) {
        disconnectClient(false);
        disconnectClient(true);
    } else {
        disconnectClient(isHolder);
    }
};

const subscribeOnMessage = (isHolder) => {
    const cmClient = cmService.getCmClient(isHolder)();

    if (!cmClient) return;

    cmClient.subscribe();

    cmClient.attachMessageHandler((message) => {
        log.info(`CmClient: isHolder: ${isHolder} new message: ${message.payloadString}`);
        const mes = converter.stringToJson(message.payloadString);

        cmMessageResponseService.onCmMessage(mes, isHolder);
    });

    cmMessageRequestService.publishInitializationEvent(isHolder);
};

const publishMessage = (message, isHolder) => {
    const cmClient = cmService.getCmClient(isHolder)();

    if (!cmClient) return;

    cmClient.publish(message);
};

const isCmClientConnected = (isHolder) => {
    const cmClient = cmService.getCmClient(isHolder)();

    if (!cmClient) return false;

    const assetId = makeSelectAssetIdByType(isHolder)(getState());

    return !!cmClient.isConnected(assetId);
};

export default {
    initCmClient,
    publishMessage,
    disconnectCmClient,
    isCmClientConnected,
};
